/**
 * Comisioane bancare — `GET /api/comisioane` si `POST /api/tariffs/:bankId/supersede`.
 *
 * Doua lucruri merita stiute despre stratul asta:
 *
 *   1. **Driftul NU se ascunde.** Coeficientii observati in fisier oscileaza (BT 0,95 / 0,96 / 0,98
 *      fata de 0,98 documentat), deci `driftCount` per banca e un semnal, nu o eroare de calcul.
 *      Hookul nu il netezeste si nu il filtreaza: numarul e informatia din care un om decide care
 *      valoare e cea corecta.
 *   2. **`supersede` e `dryRun: true` implicit pe server.** Diffull se arata INAINTE de a scrie, nu
 *      dupa. De aceea `useSupersedeTarif` nu invalideaza nimic cand raspunsul vine cu
 *      `applied: false` — o simulare nu are voie sa arunce cache-ul altcuiva.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { BankTariff, BankTariffSurcharge } from '@evomag/core/tariff';

import { api, type CuImplicite } from '../lib/api.ts';
import { chei, type CheieComisioane } from '../lib/query-keys.ts';
import type {
  SupersedeTariffInput,
  SupersedeTariffResponse,
} from '../../../../functions/api/schemas.ts';

export type { SupersedeTariffResponse };

export interface BancaComisioane {
  id: number;
  code: string;
  name: string;
  isReference: boolean;
  columnOrder: number | null;
  tariffs: BankTariff[];
  surcharges: BankTariffSurcharge[];
  /** Cate cotatii au comision calculat diferit de coeficientul observat la import. */
  driftCount: number;
}

/** Tabelul „realizat 2025": foaia statica moarta devine controlul cu care se verifica tariful. */
export interface RealizatComision {
  id: number;
  periodLabel: string;
  bankId: number | null;
  bankLabelRaw: string;
  tierCode: string;
  tierCurrency: string;
  feeTotalMinor: number | null;
  txnCount: number | null;
  avgFeeMinor: number | null;
  currency: string;
  isGrandTotal: boolean;
  legacyRaw: string | null;
}

export interface ComisionPeZi {
  bankId: number;
  bankName: string;
  businessDate: string;
  /** Comisionul CASTIGATORULUI, nu suma tuturor cotatiilor. */
  commissionMinor: number | null;
  txnCount: number;
  driftCount: number;
}

export interface RaspunsComisioane {
  period: string;
  banks: BancaComisioane[];
  actuals: RealizatComision[];
  byBankByDay: ComisionPeZi[];
}

export function useComisioane(
  parametri?: CheieComisioane,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsComisioane, Error> {
  return useQuery({
    queryKey: chei.comisioane.cu(parametri),
    queryFn: ({ signal }) =>
      api.get<RaspunsComisioane>('/comisioane', {
        interogare: {
          bankId: parametri?.bankId,
          onDate: parametri?.onDate,
          period: parametri?.period,
        },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false,
    // Tarifele se schimba de cateva ori pe an; nu au ce sa caute intr-un refetch la fiecare montare.
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// POST /api/tariffs/:bankId/supersede
// ---------------------------------------------------------------------------

export type IntrareSupersede = CuImplicite<SupersedeTariffInput, 'channel' | 'dryRun'>;

export interface VariabileSupersede {
  bankId: number;
  tarif: IntrareSupersede;
}

export function fnSupersedeTarif(variabile: VariabileSupersede): Promise<SupersedeTariffResponse> {
  return api.post<SupersedeTariffResponse>(
    `/tariffs/${variabile.bankId}/supersede`,
    variabile.tarif,
  );
}

/**
 * Inlocuieste grila de tarife a unei banci de la o data.
 *
 * Rol `admin`. Serverul inchide tarifele vechi si le creeaza pe cele noi, dar atinge DOAR blocurile
 * deschise si tranzactiile neaprobate — istoricul nu se rescrie niciodata. Cand `applied` e `false`
 * (dryRun), raspunsul e un diff de citit, nu o scriere.
 */
export function useSupersedeTarif(): UseMutationResult<
  SupersedeTariffResponse,
  Error,
  VariabileSupersede
> {
  const queryClient = useQueryClient();
  return useMutation<SupersedeTariffResponse, Error, VariabileSupersede>({
    mutationKey: chei.mutatii.supersedeTarif(),
    mutationFn: fnSupersedeTarif,
    onSuccess: (raspuns) => {
      if (!raspuns.applied) return;
      void queryClient.invalidateQueries({ queryKey: chei.comisioane.tot() });
      // Comisioanele recalculate schimba costul total si deci castigatorul din comparator.
      void queryClient.invalidateQueries({ queryKey: chei.valutar.tot() });
    },
  });
}
